import { FC, useEffect, useState } from "react";
import { Badge, Button, Form, InputGroup, Modal } from "react-bootstrap";
import {
    CreateBatchBuildData,
    CreateCuttlefishBuildVariables,
    CuttlefishBuild,
    CuttlefishConfigData,
    Project,
    ProjectVariables
} from "../../models/types";
import { Buffer } from "buffer";
import { useLocalState } from "../../graphql/hooks";
import { CuttlefishNetworkList } from "../lists/cuttlefish/CuttlefishNetworkList";
import { useApolloClient, useLazyQuery, useMutation } from "@apollo/client";
import { FETCH_CUTTLEFISH_CONFIG } from "../../graphql/queries";
import { generateCommands } from "../../common/builds";
import { CREATE_CUTTLEFISH_BUILD } from "../../graphql/mutations";
import { updateNotificationState } from "../../common/Helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChartLine,
    faCheckDouble,
    faClockRotateLeft,
    faListCheck,
    faSearch
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import semver from "semver";
import styles from "./CuttlefishBuildModal.module.scss";

interface Props {
    project: Project;
    show: boolean;
    historyPath?: string;
    onClose: () => void;
}
const defaultNameTemplate =
    "#{network}_#{build}_#{variation}_#{duration}_#{size}_#{date}";
const defaultBuildPathTemplate = "#{network}/#{size}/#{customPath}";
const npm_invalid_character_combinations =
    /\*{2,}|[\>\<\=\^\~]\s*\*|\*\s*[\>\<\=\^\~]/;
const reviewNetwork = "unity";
const reviewSizes = [2, 3, 5];

const isReviewBuild = (build: CuttlefishBuild) =>
    build.network === reviewNetwork &&
    build.size !== undefined &&
    reviewSizes.includes(build.size);

export const CuttlefishBuildModal: FC<Props> = ({
    project,
    show,
    historyPath,
    onClose
}) => {
    const client = useApolloClient();
    const navigate = useNavigate();
    const { useDarkMode } = useLocalState();
    const [containsAnalytics, updateContainsAnalytics] = useState(false);
    const [canBuild, setBuildStatus] = useState(true);
    const [builds, setBuilds] = useState<CuttlefishBuild[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [runtime, updateRuntime] = useState<string | undefined>(undefined);
    const [fetchConfig] = useLazyQuery<CuttlefishConfigData, ProjectVariables>(
        FETCH_CUTTLEFISH_CONFIG,
        {
            variables: { projectId: Number(project.id) },
            fetchPolicy: "network-only"
        }
    );
    const [createBuild] = useMutation<
        CreateBatchBuildData,
        CreateCuttlefishBuildVariables
    >(CREATE_CUTTLEFISH_BUILD);

    useEffect(() => {
        if (show) {
            setSearchTerm("");
            fetchConfig().then(result => {
                if (result.data?.getCuttlefishConfig?.cuttlefishConfig) {
                    const raw =
                        result.data.getCuttlefishConfig.cuttlefishConfig;
                    const cuttlefishContent = raw
                        ? Buffer.from(raw, "base64").toString("utf8")
                        : undefined;
                    if (cuttlefishContent !== undefined) {
                        const jsonData = JSON.parse(cuttlefishContent);
                        const cuttlefishConfig = {
                            nodes: [],
                            edges: [],
                            nameTemplate: defaultNameTemplate,
                            buildPathTemplate: defaultBuildPathTemplate
                        };
                        if (jsonData.hasOwnProperty("buildData")) {
                            const buildData = jsonData["buildData"];
                            cuttlefishConfig.nodes =
                                jsonData["buildData"]["nodes"] || [];
                            cuttlefishConfig.edges =
                                jsonData["buildData"]["edges"] || [];
                            if (buildData.hasOwnProperty("nameTemplate")) {
                                cuttlefishConfig.nameTemplate =
                                    buildData["nameTemplate"];
                            }
                            if (buildData.hasOwnProperty("buildPathTemplate")) {
                                cuttlefishConfig.buildPathTemplate =
                                    buildData["buildPathTemplate"];
                            }

                            if (jsonData.hasOwnProperty("runtime")) {
                                if (
                                    semver.valid(
                                        semver.coerce(jsonData.runtime)?.version
                                    ) &&
                                    !npm_invalid_character_combinations.test(
                                        jsonData.runtime
                                    )
                                ) {
                                    updateRuntime(jsonData.runtime);
                                } else {
                                    updateRuntime(undefined);
                                }
                            }
                        }
                        const builds = generateCommands(cuttlefishConfig);
                        if (builds !== undefined) {
                            setBuilds(builds);
                        }
                    }
                }
            });
        }
    }, [show]);

    useEffect(() => {
        let timer: any | undefined = undefined;
        if (!canBuild) {
            timer = setTimeout(() => {
                setBuildStatus(true);
            }, 2000);
        }
        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [canBuild]);

    useEffect(() => {
        const analyticsEnabled = builds.some(
            build => build.command.includes("--analytics true") && build.enabled
        );
        updateContainsAnalytics(analyticsEnabled);
    }, [builds]);

    const buildsByNetwork = builds.reduce<Record<string, CuttlefishBuild[]>>(
        (acc, build: CuttlefishBuild) => {
            if (!acc[build.network]) {
                acc[build.network] = [];
            }
            acc[build.network].push(build);
            return acc;
        },
        {}
    );

    const allBuildsEnabled = builds.length > 0 && builds.every(b => b.enabled);

    const hasReviewBuilds = builds.some(isReviewBuild);
    const reviewSetSelected =
        hasReviewBuilds && builds.every(b => b.enabled === isReviewBuild(b));

    const toggleAllBuilds = (enabled: boolean) => {
        const newBuilds = builds.map(build => {
            return { ...build, enabled: enabled };
        });
        setBuilds(newBuilds);
    };

    const selectReviewBuilds = () => {
        setBuilds(prev =>
            prev.map(build => {
                return { ...build, enabled: isReviewBuild(build) };
            })
        );
    };

    const toggleBuild = (id: number) => {
        const newBuilds = builds.map(build =>
            build.id === id ? { ...build, enabled: !build.enabled } : build
        );
        setBuilds(newBuilds);
    };

    const toggleNetwork = (network: string) => {
        const networkBuilds = buildsByNetwork[network];
        const allEnabled = networkBuilds.every(b => b.enabled);
        setBuilds(prev =>
            prev.map(b =>
                b.network === network ? { ...b, enabled: !allEnabled } : b
            )
        );
    };

    const toggleTag = (network: string, tag: string) => {
        const taggedBuilds = buildsByNetwork[network].filter(b =>
            b.tags.includes(tag)
        );
        const allEnabled = taggedBuilds.every(b => b.enabled);
        setBuilds(prev =>
            prev.map(b =>
                b.network === network && b.tags.includes(tag)
                    ? { ...b, enabled: !allEnabled }
                    : b
            )
        );
    };

    const toggleSize = (network: string, size: number) => {
        const sizedBuilds = buildsByNetwork[network].filter(
            b => b.size === size
        );
        const allEnabled = sizedBuilds.every(b => b.enabled);
        setBuilds(prev =>
            prev.map(b =>
                b.network === network && b.size === size
                    ? { ...b, enabled: !allEnabled }
                    : b
            )
        );
    };

    const getNetworkState = (network: string): "all" | "none" | "partial" => {
        const networkBuilds = buildsByNetwork[network];
        const enabledCount = networkBuilds.filter(b => b.enabled).length;
        if (enabledCount === 0) {
            return "none";
        }
        if (enabledCount === networkBuilds.length) {
            return "all";
        }
        return "partial";
    };

    const enabledCount = builds.filter(build => build.enabled).length;

    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filteredBuildsByNetwork = Object.entries(buildsByNetwork).reduce<
        Record<string, CuttlefishBuild[]>
    >((acc, [network, networkBuilds]) => {
        const matches = normalizedSearch
            ? networkBuilds.filter(build =>
                  build.name.toLowerCase().includes(normalizedSearch)
              )
            : networkBuilds;
        if (matches.length > 0) {
            acc[network] = matches;
        }
        return acc;
    }, {});

    const totalMatchCount = Object.values(filteredBuildsByNetwork).reduce(
        (sum, matches) => sum + matches.length,
        0
    );

    const build = async () => {
        if (runtime === undefined) {
            console.log("[DEBUG] missing runtime from cuttlefish config!");
            return;
        }
        const commands = builds
            .filter(build => build.enabled)
            .map(build => build.command);
        console.log("[DEBUG] commands ", commands);
        setBuildStatus(false);
        if (commands.length === 0) {
            return;
        }
        try {
            const response = await createBuild({
                variables: {
                    projectId: Number(project.id),
                    runtimeVersion: runtime,
                    commands: commands,
                    analytics: containsAnalytics
                }
            });
            console.log("createBuild response: ", response);
            updateNotificationState(client, false, true, {
                success: true,
                header: "Build Status",
                message: `Build started successfully! (id: ${response?.data?.createBuild.id})`,
                delay: 3000
            });
        } catch (error) {
            updateNotificationState(client, false, true, {
                success: false,
                header: "Build Status",
                // @ts-ignore
                message: error.message,
                delay: 3000
            });
        }
    };

    if (!show) {
        return null;
    }

    return (
        <Modal
            size="xl"
            show={show}
            onHide={onClose}
            backdrop="static"
            keyboard={true}
            className={useDarkMode ? "modal-dark" : ""}
        >
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center flex-wrap gap-3">
                    <span>Select items to create build</span>
                    <Badge
                        bg={runtime === undefined ? "danger" : "info"}
                        title="Runtime version"
                        className={styles.runtimeBadge}
                    >
                        Runtime {runtime === undefined ? "undefined" : runtime}
                    </Badge>
                    {containsAnalytics ? (
                        <Badge
                            title="Analytics enabled"
                            bg="analytics"
                            className="text-white"
                        >
                            <FontAwesomeIcon icon={faChartLine} size="sm" />
                        </Badge>
                    ) : null}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className={`${styles.modalBody} px-3 px-md-4`}>
                <div className="pb-3 pt-1 d-flex align-items-center flex-wrap gap-3">
                    <Button
                        size="sm"
                        variant={allBuildsEnabled ? "secondary" : "primary"}
                        onClick={() => toggleAllBuilds(!allBuildsEnabled)}
                        title={allBuildsEnabled ? "Deselect all" : "Select all"}
                    >
                        <FontAwesomeIcon
                            icon={faCheckDouble}
                            className="me-2"
                        />
                        {allBuildsEnabled ? "Deselect all" : "Select all"}
                    </Button>
                    <InputGroup size="sm" className={styles.searchGroup}>
                        <InputGroup.Text>
                            <FontAwesomeIcon icon={faSearch} />
                        </InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder="Filter list by name..."
                            value={searchTerm}
                            onChange={event =>
                                setSearchTerm(event.target.value)
                            }
                        />
                    </InputGroup>
                    {normalizedSearch ? (
                        <span className="small text-secondary">
                            {totalMatchCount} of {builds.length} shown
                        </span>
                    ) : null}
                    <Button
                        size="sm"
                        variant={reviewSetSelected ? "success" : "secondary"}
                        onClick={selectReviewBuilds}
                        disabled={!hasReviewBuilds}
                        className="ms-auto text-right"
                        title={
                            hasReviewBuilds
                                ? `Select review set only (${reviewNetwork} at ${reviewSizes.join("/")}MB)`
                                : `No ${reviewNetwork} builds at ${reviewSizes.join("/")}MB in this config`
                        }
                    >
                        <FontAwesomeIcon icon={faListCheck} className="me-2" />
                        Review
                    </Button>
                </div>
                <div className={`${styles.scrollableContainer} pe-1`}>
                    {Object.entries(buildsByNetwork)
                        .filter(([network]) => filteredBuildsByNetwork[network])
                        .map(([network, networkBuilds]) => {
                            const networkState = getNetworkState(network);
                            const isAllEnabled = networkState === "all";
                            const isPartial = networkState === "partial";
                            return (
                                <CuttlefishNetworkList
                                    key={network}
                                    network={network}
                                    isPartial={isPartial}
                                    isAllEnabled={isAllEnabled}
                                    useDarkMode={useDarkMode}
                                    networkBuilds={networkBuilds}
                                    visibleBuilds={
                                        filteredBuildsByNetwork[network]
                                    }
                                    toggleBuild={toggleBuild}
                                    toggleNetwork={toggleNetwork}
                                    toggleTag={toggleTag}
                                    toggleSize={toggleSize}
                                />
                            );
                        })}
                </div>
            </Modal.Body>
            <Modal.Footer
                className={`${styles.footer} d-flex align-items-center`}
            >
                <Button
                    onClick={build}
                    disabled={
                        enabledCount === 0 || !canBuild || runtime === undefined
                    }
                >
                    <span className="fw-bold">Build</span>{" "}
                    {enabledCount > 0 ? "(" + enabledCount + ")" : "(0)"}
                </Button>
                {historyPath ? (
                    <Button
                        className={styles.historyButton}
                        variant="secondary"
                        title="Go to build history"
                        onClick={() => navigate(historyPath)}
                    >
                        <FontAwesomeIcon
                            className="me-2"
                            icon={faClockRotateLeft}
                        />
                        Build History
                    </Button>
                ) : null}
            </Modal.Footer>
        </Modal>
    );
};
